// ============================================================================
// API SERVICE — Real calls to Django /api/intel/ with fingerprint identity
// ============================================================================

import { MOCK_POSTS, MOCK_STATS, MOCK_TRENDING } from "./mockData";
import { getFingerprintHeader } from "./fingerprint";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ── Helpers ───────────────────────────────────────────────────────────────────

async function api(path, options = {}) {
  const url = `${API_BASE}/api/intel${path}`;
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getFingerprintHeader(), // Persistent anon identity
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API ${res.status}`);
  }
  return res.json();
}

async function withFallback(apiCall, mockFallback) {
  try {
    return await apiCall();
  } catch (err) {
    console.warn("[API] Falling back to mock data:", err.message);
    return mockFallback();
  }
}

// ── Posts ─────────────────────────────────────────────────────────────────────

export async function fetchPosts({ type = "all", page = 1, limit = 10 } = {}) {
  return withFallback(
    async () => {
      const params = new URLSearchParams({ page, limit });
      if (type !== "all") params.set("type", type);
      const data = await api(`/posts/?${params}`);
      return { results: data.results, count: data.count, next: data.next };
    },
    () => {
      let posts = [...MOCK_POSTS];
      if (type !== "all") posts = posts.filter((p) => p.type === type);
      return { results: posts, count: posts.length, next: null };
    }
  );
}

export async function fetchPost(id) {
  return withFallback(
    () => api(`/posts/${id}/`),
    () => {
      const post = MOCK_POSTS.find((p) => p.id === id);
      if (!post) throw new Error("Post not found");
      return post;
    }
  );
}

// ── Comments ──────────────────────────────────────────────────────────────────

export async function fetchComments(postId) {
  return withFallback(
    () => api(`/posts/${postId}/comments/`),
    () => {
      const post = MOCK_POSTS.find((p) => p.id === postId);
      return post?.comments || [];
    }
  );
}

export async function createComment(postId, { text }) {
  return withFallback(
    () =>
      api(`/posts/${postId}/comments/`, {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
    () => ({
      id: `comment-${Date.now()}`,
      author: `anon-${Math.random().toString(36).slice(2, 6)}`,
      text,
      ts: "Just now",
      created_at: new Date().toISOString(),
    })
  );
}

// ── Reactions ─────────────────────────────────────────────────────────────────

export async function toggleReaction(postId, reactionKey) {
  return withFallback(
    () =>
      api(`/posts/${postId}/react/`, {
        method: "POST",
        body: JSON.stringify({ reaction: reactionKey }),
      }),
    () => ({ post_id: postId, reaction: reactionKey, toggled: true })
  );
}

// ── Stats / Sidebar ───────────────────────────────────────────────────────────

export async function fetchStats() {
  return withFallback(() => api("/stats/"), () => MOCK_STATS);
}

export async function fetchTrending() {
  return withFallback(() => api("/trending/"), () => MOCK_TRENDING);
}

// ── Search ────────────────────────────────────────────────────────────────────

export async function searchPosts(query) {
  return withFallback(
    () => api(`/posts/search/?q=${encodeURIComponent(query)}`),
    () => {
      const q = query.toLowerCase();
      const results = MOCK_POSTS.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.summary.toLowerCase().includes(q) ||
          p.tags?.some((t) => t.toLowerCase().includes(q))
      );
      return { results, count: results.length };
    }
  );
}

// ── Session ───────────────────────────────────────────────────────────────────

export async function getSession() {
  return withFallback(
    () => api("/session/"),
    () => ({ authenticated: false, user: null, role: "anonymous" })
  );
}

// ── Surveys ───────────────────────────────────────────────────────────────────

const MOCK_SURVEYS = [
  {
    id: 1,
    title: "What should Wyzcon analyze next?",
    description: "Help us prioritize our next deep-dive analysis. We publish sourced reports every 3 days — your vote decides the topic.",
    category: "next_analysis",
    question_type: "single",
    status: "active",
    closesAt: new Date(Date.now() + 3 * 86400000).toISOString(),
    isExpired: false,
    timeLeft: "3d 0h left",
    totalVotes: 47,
    uniqueVoters: 47,
    is_featured: true,
    show_results_before_close: true,
    hasVoted: false,
    interpretation: "",
    options: [
      { id: 101, text: "Pension Tier 4 vs Tier 6: The Real Dollar Difference", order: 0, votes: 18, percentage: 38.3, selected: false },
      { id: 102, text: "NYC Teacher Housing Crisis: Salary vs. Rent by Borough", order: 1, votes: 14, percentage: 29.8, selected: false },
      { id: 103, text: "Per-Session Pay Rates: Are They Keeping Up?", order: 2, votes: 8, percentage: 17.0, selected: false },
      { id: 104, text: "Health Insurance Changes: What the New Plans Really Cover", order: 3, votes: 5, percentage: 10.6, selected: false },
      { id: 105, text: "Classroom Funding: How Much Teachers Spend Out of Pocket", order: 4, votes: 2, percentage: 4.3, selected: false },
    ],
  },
  {
    id: 2,
    title: "How do you feel about the current UFT contract?",
    description: "The 2022-2027 contract is midway through. Anonymous — share your honest assessment.",
    category: "sentiment",
    question_type: "single",
    status: "active",
    closesAt: new Date(Date.now() + 5 * 86400000).toISOString(),
    isExpired: false,
    timeLeft: "5d 0h left",
    totalVotes: 23,
    uniqueVoters: 23,
    is_featured: false,
    show_results_before_close: true,
    hasVoted: false,
    interpretation: "",
    options: [
      { id: 201, text: "It's a fair deal given the circumstances", order: 0, votes: 3, percentage: 13.0, selected: false },
      { id: 202, text: "The raises don't match inflation — we're falling behind", order: 1, votes: 11, percentage: 47.8, selected: false },
      { id: 203, text: "Better than expected but still not enough", order: 2, votes: 5, percentage: 21.7, selected: false },
      { id: 204, text: "Deeply disappointed — leadership settled too low", order: 3, votes: 3, percentage: 13.0, selected: false },
      { id: 205, text: "Haven't looked into the details enough to say", order: 4, votes: 1, percentage: 4.3, selected: false },
    ],
  },
  {
    id: 3,
    title: "Rate your experience with DOE admin support this year",
    description: "On a scale of 1-5, how supported do you feel by your school's administration?",
    category: "experience",
    question_type: "rating",
    status: "active",
    closesAt: new Date(Date.now() + 4 * 86400000).toISOString(),
    isExpired: false,
    timeLeft: "4d 0h left",
    totalVotes: 31,
    uniqueVoters: 31,
    is_featured: false,
    show_results_before_close: true,
    hasVoted: false,
    interpretation: "",
    options: [
      { id: 301, text: "1 — No support at all", order: 0, votes: 9, percentage: 29.0, selected: false },
      { id: 302, text: "2 — Minimal support", order: 1, votes: 8, percentage: 25.8, selected: false },
      { id: 303, text: "3 — Adequate", order: 2, votes: 7, percentage: 22.6, selected: false },
      { id: 304, text: "4 — Good support", order: 3, votes: 5, percentage: 16.1, selected: false },
      { id: 305, text: "5 — Excellent support", order: 4, votes: 2, percentage: 6.5, selected: false },
    ],
  },
  {
    id: 4,
    title: "What are your biggest daily frustrations? (Select all that apply)",
    description: "Pick everything that hits. We'll use this to prioritize our tools and coverage.",
    category: "priority",
    question_type: "multiple",
    status: "active",
    closesAt: new Date(Date.now() + 4 * 86400000).toISOString(),
    isExpired: false,
    timeLeft: "4d 0h left",
    totalVotes: 89,
    uniqueVoters: 34,
    is_featured: false,
    show_results_before_close: false,
    allow_multiple: true,
    hasVoted: false,
    interpretation: "",
    options: [
      { id: 401, text: "Grading workload — never enough hours", order: 0, votes: 28, percentage: 31.5, selected: false },
      { id: 402, text: "Admin paperwork and compliance tasks", order: 1, votes: 22, percentage: 24.7, selected: false },
      { id: 403, text: "Class sizes that keep growing", order: 2, votes: 17, percentage: 19.1, selected: false },
      { id: 404, text: "Lack of support staff (counselors, paras)", order: 3, votes: 12, percentage: 13.5, selected: false },
      { id: 405, text: "Compensation not matching cost of living", order: 4, votes: 7, percentage: 7.9, selected: false },
      { id: 406, text: "Student behavior and discipline challenges", order: 5, votes: 3, percentage: 3.4, selected: false },
    ],
  },
  {
    id: 5,
    title: "Should NYC teachers get housing subsidies?",
    description: "Several cities offer teacher housing programs. Should NYC implement one?",
    category: "policy",
    question_type: "single",
    status: "closed",
    closesAt: new Date(Date.now() - 86400000).toISOString(),
    isExpired: true,
    timeLeft: "Closed",
    totalVotes: 100,
    uniqueVoters: 100,
    is_featured: false,
    show_results_before_close: true,
    hasVoted: false,
    interpretation: "89% of respondents support some form of housing assistance for NYC teachers. The strongest support (42%) is for direct subsidies, followed by dedicated affordable housing (31%). This aligns with our purchasing power analysis showing that a mid-career teacher's rent burden has crossed the 30% threshold. We'll be publishing a full housing policy analysis next week examining models from San Francisco, Denver, and Newark.",
    options: [
      { id: 501, text: "Yes — direct monthly housing subsidies", order: 0, votes: 42, percentage: 42.0, selected: false },
      { id: 502, text: "Yes — dedicated affordable housing for teachers", order: 1, votes: 31, percentage: 31.0, selected: false },
      { id: 503, text: "Yes — but only for new teachers in their first 5 years", order: 2, votes: 16, percentage: 16.0, selected: false },
      { id: 504, text: "No — focus on raising base salary instead", order: 3, votes: 8, percentage: 8.0, selected: false },
      { id: 505, text: "No — housing subsidies aren't the government's role", order: 4, votes: 3, percentage: 3.0, selected: false },
    ],
  },
];

export async function fetchSurveys() {
  return withFallback(
    () => api("/surveys/"),
    () => MOCK_SURVEYS
  );
}

export async function fetchSurvey(id) {
  return withFallback(
    () => api(`/surveys/${id}/`),
    () => MOCK_SURVEYS.find((s) => s.id === id) || null
  );
}

export async function castVote(surveyId, optionId) {
  return withFallback(
    () =>
      api(`/surveys/${surveyId}/vote/`, {
        method: "POST",
        body: JSON.stringify({ option_id: optionId }),
      }),
    () => {
      // Mock: return a toggled version
      const survey = MOCK_SURVEYS.find((s) => s.id === surveyId);
      if (!survey) return { action: "failed", survey: null };
      return { action: "added", survey: { ...survey } };
    }
  );
}

export async function fetchSurveyResults(surveyId) {
  return withFallback(
    () => api(`/surveys/${surveyId}/results/`),
    () => MOCK_SURVEYS.find((s) => s.id === surveyId) || null
  );
}
